import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Optional,
  ElementRef,
  ViewChild,
  Inject,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
  Validators,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocomplete as MatAutocomplete,
} from '@angular/material/legacy-autocomplete';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  BehaviorSubject,
  concat,
  merge,
  combineLatest,
  Observable,
  of,
  Subject,
  Subscription,
  pipe,
  zip,
  throwError,
} from 'rxjs';
import {
  map,
  startWith,
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
  finalize,
  takeUntil,
} from 'rxjs/operators';

import { IUserAccountInterface } from '../../../client/interfaces/client-company.interface';
import {
  LocalStorageKeys,
  ApprovalUserTemplateApiService,
  OptionInterface,
  SnackBarConfig,
  UserApiService,
} from '../../../shared';
import { trackByValue, trackByControlValue, trackById } from '../../../shared/util';
import { emptyStringValidator } from '../../../shared/util/custom-validation.utils';
import {
  IApprovalUserTemplate,
  IApprovalUserTemplateAssignment,
} from '../../interfaces/approval.interface';

@Component({
  selector: 'lum-approval-user-template-form',
  templateUrl: './approval-user-template-form.component.html',
  styleUrls: ['./approval-user-template-form.component.scss'],
})
export class ApprovalUserTemplateFormComponent implements OnInit, OnDestroy {
  @ViewChild('autocompleteInput')
  autocompleteInput :ElementRef<HTMLInputElement>;

  @ViewChild('auto')
  auto :MatAutocomplete;

  approvalUserTemplateId :number = 0;

  approvalUserTemplate :IApprovalUserTemplate;

  submittingForm :boolean = false;
  loading :boolean = false;

  form :UntypedFormGroup;

  selectedUsers :IUserAccountInterface[] = [];

  users :IUserAccountInterface[] = [];
  inputValue :string = '';
  private searchUpdated$ = new BehaviorSubject<string>('');
  private readonly _userLoading = new BehaviorSubject<boolean>(<boolean>false);
  userLoading$ = this._userLoading.asObservable();

  trackByValue = trackByValue;
  trackById = trackById;
  trackByControlValue = trackByControlValue;

  private onDestroy$ = new Subject();

  constructor(
    private approvalUserTemplateApiService :ApprovalUserTemplateApiService,
    private route :ActivatedRoute,
    private router :Router,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    private userApiService :UserApiService,
    @Optional() public dialogRef :MatDialogRef<ApprovalUserTemplateFormComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data :IApprovalUserTemplate,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.initComponentInputs();
    this.onSearchUpdate();
    this.getRouterParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  markFormArrayAsTouched(formArray :UntypedFormArray) :void {
    formArray.controls.forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  markFormGroupAsTouched(formGroup :UntypedFormGroup) :void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof UntypedFormControl) {
        control.markAsTouched();
      } else if (control instanceof UntypedFormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof UntypedFormArray) {
        this.markFormArrayAsTouched(control);
      }
    });
  }

  onSubmit() :void {
    this.form.markAllAsTouched();

    if (this.form.get('userIds')?.value.length === 0) {
      this.snackBar.open('At least one approver must be added.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (this.form.invalid) {
      return;
    }

    this.submittingForm = true;

    try {
      if (!this.approvalUserTemplateId) {
        this.approvalUserTemplateApiService
          .create({
            label: this.form.value.label,
            userIds: this.form.value.userIds,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open(
                'Approval User Template successfully added.',
                'Success',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );

              if (this.isDialog) {
                this.dialogRef.close(response);
              } else {
                this.router.navigate(['settings/approval-user-template-settings']);
                this.cd.detectChanges();
              }
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      } else {
        this.approvalUserTemplateApiService
          .update(this.approvalUserTemplateId, {
            label: this.form.value.label,
            userIds: this.form.value.userIds,
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.submittingForm = false;
              this.snackBar.open(
                'Approval User Template successfully updated.',
                'Success',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );

              if (this.isDialog) {
                this.dialogRef.close(response);
              } else {
                this.router.navigate(['settings/approval-user-template-settings']);
                this.cd.detectChanges();
              }
            },
            error: (response) => {
              this.submittingForm = false;
              this.snackBar.open(response.error, 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              this.cd.detectChanges();
            },
          });
      }
    } catch (error :any) {
      this.submittingForm = false;
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.cd.detectChanges();
    }
  }

  private initForm() :void {
    this.form = this.fb.group({
      label: [null, [Validators.required, emptyStringValidator()]],
      userIds: [[], Validators.required],
    });
  }

  private setFormValue() :void {
    if (this.approvalUserTemplate && this.approvalUserTemplate.id) {
      this.form?.get('label')?.setValue(this.approvalUserTemplate.label);

      const userIds =
        this.approvalUserTemplate?.approvalUserTemplateAssignments?.map(
          (assignment :IApprovalUserTemplateAssignment) => assignment.userId,
        ) ?? [];

      this.form.get('userIds')?.setValue(userIds);

      this.selectedUsers =
        this.approvalUserTemplate?.approvalUserTemplateAssignments
          ?.map((assignment :IApprovalUserTemplateAssignment) => assignment.user)
          .filter((user) :user is IUserAccountInterface => user !== undefined) ??
        [];

      this.cd.detectChanges();
    }
  }

  private loadData(id :number) :void {
    this.loading = true;
    this.approvalUserTemplateApiService
      .getById(id, {
        relations: 'approvalUserTemplateAssignments.user',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.approvalUserTemplate = response;
          this.setFormValue();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
      });
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { approvalUserTemplateId } = params;
        if (approvalUserTemplateId) {
          this.approvalUserTemplateId = approvalUserTemplateId;
          this.loadData(this.approvalUserTemplateId);
        }
        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onSearchKeywordChange(event :any) :void {
    this.searchUpdated$.next(event.target.value);
  }

  private onSearchUpdate() :void {
    this._userLoading.next(true);

    this.searchUpdated$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchValue) =>
          this.userApiService.search({ searchQuery: searchValue }).pipe(
            catchError((error) => {
              this.snackBar.open(
                error?.error ? error.error : 'Error searching record',
                'Close',
                { duration: SnackBarConfig.DEFAULT.duration },
              );

              return of(this.users);
            }),
          ),
        ),
        map((response) => response || []),
        finalize(() => this._userLoading.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe((response) => {
        this.users = response.data;
        this._userLoading.next(false);
      });
  }

  onSelectOption(event :MatAutocompleteSelectedEvent) :void {
    this.inputValue = '';
    const selectedUser :IUserAccountInterface = event.option.value;

    if (!this.selectedUsers.find((user) => user.id === selectedUser.id)) {
      this.selectedUsers.push(selectedUser);
      this.form.get('userIds')?.setValue(this.selectedUsers.map((user) => user.id));
    }

    this.autocompleteInput.nativeElement.value = '';
    this.autocompleteInput.nativeElement.blur();
  }

  removeSelectedUser(userToRemove :IUserAccountInterface) :void {
    this.selectedUsers = this.selectedUsers.filter(
      (user) => user.id !== userToRemove.id,
    );
    this.form.get('userIds')?.setValue(this.selectedUsers.map((user) => user.id));
  }

  private initComponentInputs() :void {
    if (this.isDialog && this.data) {
      this.approvalUserTemplate = this.data;
      this.approvalUserTemplateId = this.data.id;
      this.setFormValue();
      this.cd.detectChanges();
    }
  }

  get isDialog() :boolean {
    return this.dialogRef?.componentInstance === this;
  }
}
